import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, Label, Menu, Button } from 'semantic-ui-react';
import './index.css'
import logo from '../src/assets/authorator_logo_no-dot.png';
import useIsLoggedIn from './isLoggedIn';
import AllowlistBadge from './components/AllowlistBadge';
import { getUserData } from './slices/userSlice';
import { selectCartItemCount } from './slices/cartSlice';
import { selectActiveRole } from './slices/userRoleSlice';
import { hasFeatureAccess } from './utils/roleUtils';
import { isRegistrationEnabled } from './utils/featureFlags';
import RoleSwitcher from './components/RoleSwitcher';

export function Navbar() {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const cartItemCount = useSelector(selectCartItemCount);
  const activeRole = useSelector(selectActiveRole);
  const navigate = useNavigate();
  const token = useIsLoggedIn();
  const isLogged = Boolean(token);

  // User data initialization is now handled at the App level
  // No need to fetch user data here anymore

  // Get role-specific navigation items
  const getRoleSpecificNavItems = () => {
    if (!isLogged) return [];

    const commonItems = [
      { to: "/opportunities", label: "Marketplace", icon: "shopping bag", feature: null },
      // { to: "/dashboard", label: "Dashboard", icon: "dashboard", feature: null },
      // { to: "/profile", label: "My Profile", icon: "user", feature: null }
    ];

    // Admin-specific items
    const adminItems = [
      { to: "/landing/admin", label: "Admin Home", icon: "home", feature: null },
      { to: "/dashboard", state: { menuSelection: 'USER-MANAGEMENT' }, label: "System Management", icon: "users", feature: "userManagement" }
    ];

    // Book Creator-specific items
    const bookCreatorItems = [
      { to: "/landing/book-creator", label: "Creator Home", icon: "home", feature: null },

      // { to: "/dashboard", state: { menuSelection: 'BOOK-DETAILS' }, label: "My Books", icon: "book", feature: "bookDetails" },
      // { to: "/dashboard", state: { menuSelection: 'VOICE-ACTOR-ASSIGNMENTS' }, label: "Voice Actors", icon: "microphone", feature: "voiceActorAssignments" },
      // { to: "/opportunities/create", label: "Create Opportunity", icon: "plus", feature: null },
      // { to: "/opportunities/manage", label: "Manage Opportunities", icon: "list", feature: null }
    ];

    // Voice Actor-specific items
    const voiceActorItems = [
      { to: "/landing/voice-actor", label: "Actor Home", icon: "home", feature: null },
      // { to: "/dashboard", state: { menuSelection: 'MY-ASSIGNMENTS' }, label: "My Assignments", icon: "tasks", feature: "myAssignments" },
      // { to: "/dashboard", state: { menuSelection: 'VOICE-LAB-MY-RECORDINGS' }, label: "My Recordings", icon: "file audio", feature: "voiceLabMyRecordings" },
      // { to: "/opportunities", label: "Voice Opportunities", icon: "bullhorn", feature: null }  // Duplicate of Marketplace
    ];

    // Return items based on active role
    switch (activeRole) {
      case 'admin':
        return [...commonItems, ...adminItems];
      case 'bookCreator':
        return [...commonItems, ...bookCreatorItems];
      case 'voiceActor':
        return [...commonItems, ...voiceActorItems];
      default:
        return commonItems;
    }
  };

  const isHideClassName = isLogged ? "floatLeft" : "hideElement";
  const isLoggedInClassName = isLogged ? "hideElement" : "floatLeft";

  const onLogoClick = () => {
    navigate('/landing') // Link to the guest landing page
  }

  let userTitle = "";
  if (userProfile !== null && userProfile.data !== null) {
    userTitle = `Welcome ${userProfile.data.title}`;
  }
  else {
    userTitle = "Please login, or register..."
  }

  // Get role-specific navigation items
  const roleNavItems = getRoleSpecificNavItems();

  return (
    <div className="navBarHeaderContainer">
      <div className="logoContainer">
        <div onClick={() => onLogoClick()} style={{ cursor: 'pointer' }}>
          <img className="headerLogo" src={logo} alt="Authorator-ai" />
          <div className="headNameContainer">
            <span className="headAppName">Authorator-ai</span><br />
            <span className="headAppSubTitle">AI Narration Accelerator</span>
            <span className="headAppSubTitle" style={{ marginLeft: "30px" }}>{userTitle}</span>
          </div>
        </div>

      </div>
      <div className="top-menu">
        <nav id="top-menu-nav">
          <ul id="top-menu-ul" className="top-menu-ul">
            <li className="top-menu-li" id="menu-item-1"><Link to="/landing" >Home</Link></li>
            <li className="top-menu-li" id="menu-item-2"><Link to="/about" >About</Link></li>
            <li className="top-menu-li" id="menu-item-2"><Link to="/article" >Info</Link></li>

            {/* Login/Register links - shown to non-logged-in users.
                Phase C.5 (Guest Mode) — the Register link is suppressed when
                the build was made with REACT_APP_REGISTRATION_ENABLED=false
                (deployed WIP previews). The /register URL still resolves so
                operator-issued direct links keep working; only the public
                CTA disappears. The Login link is always visible — existing
                users (including the seeded Guest accounts) need to sign in.
                See client/src/utils/featureFlags.js for the fail-open
                semantics shared with the server-side gate. */}
            {!isLogged && (
              <>
                {isRegistrationEnabled() && (
                  <li className="top-menu-li" id="menu-item-3"><Link to="/register" >Register</Link></li>
                )}
                <li className="top-menu-li" id="menu-item-4"><Link to="/login" >Login</Link></li>
              </>
            )}

            {/* Role-specific navigation items - shown to logged-in users */}
            {isLogged && roleNavItems.map((item, index) => (
              <li className="top-menu-li" key={`role-item-${index}`}>
                <Link to={item.to} state={item.state}>
                  <Icon name={item.icon} style={{ marginRight: '5px' }} />
                  {item.label}
                </Link>
              </li>
            ))}

            {/* Allowlist standing — small, and mostly quiet. It says something worth reading in
                exactly one case: signed in to an environment that does NOT enforce the allowlist,
                with an account that production would refuse. That is the state you cannot discover
                any other way, and the one you want to find before inviting a voice actor rather
                than after. See components/AllowlistBadge.jsx. */}
            {isLogged && (
              <li className="top-menu-li" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <AllowlistBadge />
              </li>
            )}

            {/* Shopping cart - shown to logged-in users */}
            {isLogged && (
              <li className="top-menu-li shopping-cart-icon" >
                <Link
                  to="/dashBoard"
                  state={{ menuSelection: 'CHECKOUT' }}
                  style={{ position: 'relative', display: 'inline-block' }}
                >
                  <Icon
                    name='shopping cart'
                    color={cartItemCount > 0 ? 'blue' : 'grey'}
                    size='large'
                  />
                  {cartItemCount > 0 && (
                    <Label circular color='red' size='mini' style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      padding: '4px 6px',
                      fontSize: '0.7em'
                    }}>
                      {cartItemCount}
                    </Label>
                  )}
                </Link>
              </li>
            )}

            {/* Logout - shown to logged-in users */}
            {isLogged && <li className="top-menu-li"><Link to="/logout">Logout</Link></li>}

            {/* Role switcher - shown to logged-in users */}
            {isLogged && (
              <li className="top-menu-li">
                <RoleSwitcher location="navbar" />
              </li>
            )}
          </ul>

        </nav>
      </div>
    </div>
  )
}
